// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Autocomplete, TextField } from '@mui/material';
import PropTypes from 'prop-types';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';

const UserMultiSelectFieldComponent = ({
  value,
  onChange,
  usersList = EMPTY_ARRAY,
  label,
  titleLabel,
  attribute,
  entityDetailsFormikForm,
  isLoadingConstraints = false,
  required = false,
  formData = EMPTY_OBJECT,
  entityData = EMPTY_OBJECT,
  loader,
}) => {
  const { getUsersAttributesData } = useAttribute();
  const [apiUsersList, setApiUsersList] = useState(EMPTY_ARRAY);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const skipConstraintLogic = false;

  const errorHelperText = useMemo(
    () =>
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      ),
    [
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      attribute?.id,
    ]
  );

  const hasError = useMemo(() => Boolean(errorHelperText), [errorHelperText]);

  const filteredUsersList = useMemo(() => {
    // Use API users list when constraint logic is not skipped and API data is available
    const currentUsersList =
      !skipConstraintLogic && apiUsersList?.length > 0
        ? apiUsersList
        : usersList;

    // Filter valid users and remove duplicates based on username
    const uniqueUsernames = new Set();
    return currentUsersList
      .filter((user) => {
        if (user?.username && !uniqueUsernames.has(user.username)) {
          uniqueUsernames.add(user.username);
          return true;
        }
        return false;
      })
      .map((user) => ({
        label: user.username,
        value: user.user_id,
      }));
  }, [usersList, apiUsersList, skipConstraintLogic]);

  const fetchUsersData = useCallback(() => {
    if (isLoading || skipConstraintLogic) {
      return;
    }
    setIsLoading(true);
    const payload = {
      source_attribute_id: Number.parseInt(attribute?.id),
      form_data: formData,
      entity_id: entityData?.entity_id,
      entity_type: entityData?.entity_type,
      skip_constraint_logic: skipConstraintLogic,
    };

    getUsersAttributesData(payload, loader)
      .then((res) => {
        if (res?.data) {
          setApiUsersList(res.data);
        } else {
          setApiUsersList(EMPTY_ARRAY);
        }
      })
      .catch(() => {
        setApiUsersList(EMPTY_ARRAY);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    attribute?.id,
    formData,
    getUsersAttributesData,
    isLoading,
    entityData,
    loader,
    skipConstraintLogic,
  ]);

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true);
    fetchUsersData();
  }, [fetchUsersData]);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, [setIsDropdownOpen]);

  const selectedUsers = useMemo(() => {
    if (!Array.isArray(value)) return [];

    return filteredUsersList.filter((user) => value?.includes(user?.value));
  }, [value, filteredUsersList]);

  const handleChange = useCallback(
    (_, newSelectedUsers) => {
      onChange(newSelectedUsers?.map((user) => user?.value));
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    const isEffectivelyEmpty =
      !value ||
      (Array.isArray(value) &&
        (value.length === 0 || value.every((v) => v == null)));
    if (isEffectivelyEmpty) {
      onChange(null);
      setTimeout(() => {
        entityDetailsFormikForm?.setFieldTouched(attribute?.id, true, true);
      }, 0);
    } else {
      entityDetailsFormikForm?.setFieldTouched(attribute?.id, true, true);
    }
  }, [entityDetailsFormikForm, attribute?.id, value, onChange]);

  const renderUserSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        error={hasError}
        helperText={errorHelperText}
        InputProps={{
          ...params.InputProps,
          placeholder: 'Select user',
        }}
        InputLabelProps={{
          shrink: true,
        }}
      />
    ),
    [label, hasError, errorHelperText]
  );

  // Re-fetch users and validate selection when constraints finish loading, this is added because when constraints are applied, the available options might change based on the constraint logic and we need to make sure that the selected value is still valid with the new options, if not we need to clear the selection.
  const prevIsLoadingConstraintsRef = useRef(isLoadingConstraints);
  useEffect(() => {
    const wasLoading = prevIsLoadingConstraintsRef.current;
    prevIsLoadingConstraintsRef.current = isLoadingConstraints;

    // When constraints transition from loading → done, re-fetch users
    if (
      wasLoading &&
      !isLoadingConstraints &&
      !skipConstraintLogic &&
      attribute?.id
    ) {
      const payload = {
        source_attribute_id: Number.parseInt(attribute?.id),
        form_data: formData,
        entity_id: entityData?.entity_id,
        entity_type: entityData?.entity_type,
        skip_constraint_logic: skipConstraintLogic,
      };

      getUsersAttributesData(payload, false)
        .then((res) => {
          if (res?.data) {
            setApiUsersList(res.data);
            // Remove invalid selections from current value
            if (Array.isArray(value) && value.length > 0) {
              const validUserIds = new Set(
                res.data.map((user) => user?.user_id)
              );
              const validValues = value.filter((v) => validUserIds.has(v));
              if (validValues.length !== value.length) {
                onChange(validValues.length > 0 ? validValues : null);
              }
            }
          } else {
            setApiUsersList(EMPTY_ARRAY);
            if (Array.isArray(value) && value.length > 0) {
              onChange(null);
            }
          }
        })
        .catch(() => {});
    }
  }, [isLoadingConstraints]);

  return (
    <Autocomplete
      fullWidth
      value={selectedUsers || []}
      options={isLoading ? EMPTY_ARRAY : filteredUsersList}
      popupIcon={<DropdownArrow />}
      renderInput={renderUserSelectTextField}
      onChange={handleChange}
      onBlur={handleBlur}
      multiple
      disabled={isLoadingConstraints}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      open={isDropdownOpen}
      loading={isLoading}
      loadingText="Loading users..."
    />
  );
};

UserMultiSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  // Add prop types validation
  attribute: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    setFieldTouched: PropTypes.func.isRequired,
  }),
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
      user_id: PropTypes.number,
    })
  ),
  isLoadingConstraints: PropTypes.bool,
  required: PropTypes.bool,
  username: PropTypes.string.isRequired,
  user_id: PropTypes.number.isRequired,
  formData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  entityData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loader: PropTypes.bool,
};

export default UserMultiSelectFieldComponent;
